/** Consent screen for apps using authorization code + PKCE (M15-05). */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Alert, Button, Card, Skeleton } from '../../components/ui';
import { errorText } from './shared';

export function OAuthConsentPage(): JSX.Element {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const [denied, setDenied] = useState(false);
  const info = useQuery({
    queryKey: ['oauth-app', clientId, redirectUri],
    queryFn: () => api<{ name: string; scopes: { key: string; label: string }[] }>(`/oauth/authorize?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
    enabled: Boolean(clientId && redirectUri),
    retry: false,
  });
  const requested = (params.get('scope') ?? '').split(/\s+/).filter(Boolean);
  const shown = info.data?.scopes.filter((s) => !requested.length || requested.includes(s.key)) ?? [];
  const allow = useMutation({
    mutationFn: () => api<{ redirectTo: string }>('/oauth/authorize', {
      method: 'POST',
      body: { clientId, redirectUri, codeChallenge: params.get('code_challenge') ?? '', codeChallengeMethod: params.get('code_challenge_method') ?? '', scope: params.get('scope') ?? undefined, state: params.get('state') ?? undefined },
    }),
    onSuccess: (r) => window.location.assign(r.redirectTo),
  });
  const deny = () => {
    setDenied(true);
    try {
      const u = new URL(redirectUri);
      u.searchParams.set('error', 'access_denied');
      if (params.get('state')) u.searchParams.set('state', params.get('state')!);
      window.location.assign(u.toString());
    } catch {
      /* no valid redirect: stay here */
    }
  };
  return (
    <div className="mx-auto max-w-lg py-8">
      <Card>
        {!clientId || !redirectUri ? <Alert tone="critical">This link is missing client_id or redirect_uri.</Alert> : info.isLoading ? <Skeleton className="h-40 w-full" /> : info.error ? <Alert tone="critical">{errorText(info.error)}</Alert> : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-brand-50 p-2 text-brand-700 dark:bg-slate-800 dark:text-brand-100"><ShieldCheck aria-hidden="true" className="h-6 w-6" /></span>
              <div>
                <h1 className="text-lg font-bold text-slate-900 dark:text-white">{info.data!.name} wants access</h1>
                <p className="text-sm text-slate-500">to {user?.companyName} as {user?.name}</p>
              </div>
            </div>
            <div>
              <p className="mb-1 text-sm font-medium">It will be able to:</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-200">{shown.map((s) => <li key={s.key}>{s.label}</li>)}</ul>
              <p className="mt-2 text-xs text-slate-500">Never more than your own role allows, and never approve anything. Access lasts until you or an administrator revoke it.</p>
            </div>
            {allow.error && <Alert tone="critical">{errorText(allow.error)}</Alert>}
            <p className="break-all text-xs text-slate-500">You will return to {redirectUri}</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" disabled={denied} onClick={deny}>Deny</Button>
              <Button busy={allow.isPending} onClick={() => allow.mutate()}>Allow</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
